import {ButtonHTMLAttributes, FC, useId} from 'react'
import classNames from 'classnames'

import {ArrowRightSmIcon} from '../../Icons'
import {LoadingSpinner} from '../../Loaders'
import {TooltipBody, TooltipWrapper} from '../../Tooltips'

export interface DownloadButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  isLoading?: boolean
  isDisabled?: boolean
  label?: string
  hovered?: boolean
  tooltipContent?: string
  tooltipId?: string
  ariaLabel?: string
  ariaLabelLoading?: string
  className?: string
  buttonClassName?: string
}

const DownloadButton: FC<DownloadButtonProps> = ({
  isLoading = false,
  onClick,
  isDisabled = false,
  label = 'Download',
  hovered = false,
  tooltipContent,
  tooltipId,
  ariaLabel,
  ariaLabelLoading = 'Loading',
  className,
  buttonClassName,
  ...buttonProps
}) => {
  const generatedTooltipId = useId()
  const resolvedTooltipId = tooltipId ?? `download-button-${generatedTooltipId}`

  const showTooltip = Boolean(isDisabled && tooltipContent)
  const isActiveState = !isLoading && !isDisabled
  const isHoveredState = isActiveState && hovered
  const buttonClasses = classNames(
    'border rounded-full flex justify-center items-center w-8 h-8 outline-none group',
    buttonClassName,
    {
      'border-white w-8 hover:py-1 hover:pl-3 hover:pr-0.75 hover:w-auto focus-visible:py-1 focus-visible:pl-3 focus-visible:pr-0.75 focus-visible:w-auto':
        isActiveState,
      'border-white py-1 pl-3 pr-0.75 w-auto': isHoveredState,
      'border-gray-1100 hover:py-0 hover:pl-0 hover:pr-0 hover:w-8': isDisabled,
      'gap-0.75 py-1 pl-3 pr-2 w-auto hover:pr-2': isLoading,
    },
  )

  const labelClasses = classNames(
    'text-sm karla font-bold w-0 overflow-hidden',
    {
      'w-auto': isLoading || isHoveredState,
      'group hover:w-0': isDisabled,
      'group-hover:w-auto group-focus-visible:w-auto': isActiveState,
    },
  )

  return (
    <>
      <TooltipWrapper
        id={resolvedTooltipId}
        variant="dark"
        place="left"
        hidden={!showTooltip}
        delayShow={500}
      >
        <TooltipBody>{showTooltip ? tooltipContent : ''}</TooltipBody>
      </TooltipWrapper>
      <div
        data-tooltip-id={resolvedTooltipId}
        className={classNames('inline-block', className)}
      >
        <button
          {...buttonProps}
          disabled={isDisabled}
          type="button"
          aria-label={ariaLabel ?? label}
          onClick={onClick}
          className={buttonClasses}
        >
          <span className={labelClasses}>{label}</span>
          {!isLoading && (
            <ArrowRightSmIcon
              classes={classNames('transform rotate-90', {
                'group-hover:text-dope-white': isActiveState,
                'text-dope-white': isHoveredState,
              })}
              strokeColor={isDisabled ? 'text-gray-1100' : 'text-white'}
              strokeWidth={1.7}
              width={24}
              height={24}
              ariaLabel={ariaLabel ?? label}
            />
          )}
          {isLoading && (
            <LoadingSpinner
              type="TailSpin"
              color="#ffffff"
              height={16}
              width={16}
              isLoading={isLoading}
              ariaLabel={ariaLabelLoading ?? ariaLabel ?? label}
            />
          )}
        </button>
      </div>
    </>
  )
}

export {DownloadButton}
